import React, { useState, FormEvent } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useRouter, Link } from '../../router/Router';
import { auth } from '../../firebase/config/firebase';
import { isAdminUser } from '../../auth/RouteGuards';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ArrowLeft, LogIn } from 'lucide-react';

const WhatsAppIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    style={{ flexShrink: 0 }}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.456 5.711 1.457h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const Login: React.FC = () => {
  const { login, isLoading, error, clearError } = useAuth();
  const { navigate } = useRouter();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setClientError(null);
    clearError();

    const rawInput = email.trim();

    if (!rawInput) {
      setClientError('يرجى إدخال رقم الهاتف أو البريد الإلكتروني أو اسم المستخدم.');
      return;
    }

    if (!password) {
      setClientError('يرجى إدخال كلمة المرور.');
      return;
    }

    const success = await login({
      email: rawInput,
      password,
    });

    if (success) {
      const currentUser = auth.currentUser;
      const userEmail = (currentUser?.email || '').toLowerCase().trim();
      if (isAdminUser({ email: userEmail })) {
        navigate('/admin');
      } else {
        navigate('/student');
      }
    }
  };

  const activeError = clientError || error?.messageAr;

  return (
    <div className="auth-page-wrapper" dir="rtl">
      <div className="auth-card">
        {/* Header / Brand */}
        <div className="auth-card-header">
          <div className="auth-brand-badge" style={{ padding: '0', overflow: 'hidden', width: '56px', height: '56px', borderRadius: '16px', margin: '0 auto 1rem' }}>
            <img src="/oto-logo.jpg" alt="OTO" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <h1 className="auth-title">تسجيل الدخول</h1>
          <p className="auth-subtitle">
            مرحبًا بك في <strong>OTO STUDENTS</strong> — منصة نقل طلاب الجامعات
          </p>
        </div>

        {/* Error Alert */}
        {activeError && (
          <div className="auth-error-banner" role="alert">
            <AlertCircle className="auth-error-icon" size={20} />
            <span>{activeError}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          {/* Email / Username / Phone Field */}
          <div className="form-group">
            <label htmlFor="login-email" className="form-label">
              اسم المستخدم أو رقم الهاتف أو البريد الإلكتروني
            </label>
            <div className="input-with-icon">
              <Mail className="input-icon" size={18} />
              <input
                id="login-email"
                type="text"
                className="form-input"
                placeholder="اسم المستخدم أو الهاتف أو البريد (مثال: wahdan2020 أو 010...)"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                autoComplete="username email tel"
                autoFocus
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="form-group">
            <div className="label-with-link">
              <label htmlFor="login-password" className="form-label">
                كلمة المرور
              </label>
              <Link to="/forgot-password" className="forgot-password-link">
                نسيت كلمة المرور؟
              </Link>
            </div>
            <div className="input-with-icon">
              <Lock className="input-icon" size={18} />
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (clientError) setClientError(null);
                }}
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="btn-primary auth-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="btn-loading-content">
                <span className="spinner-inline"></span>
                جاري تسجيل الدخول...
              </span>
            ) : (
              <span className="btn-normal-content">
                <LogIn size={18} />
                تسجيل الدخول
              </span>
            )}
          </button>
        </form>

        {/* Footer switch to register */}
        <div className="auth-card-footer">
          <p>
            ليس لديك حساب؟{' '}
            <Link to="/register" className="auth-switch-link">
              إنشاء حساب جديد
            </Link>
          </p>
          <div className="back-home-link-box">
            <Link to="/" className="back-home-link">
              <ArrowLeft size={16} />
              العودة للرئيسية
            </Link>
          </div>

          {/* WhatsApp Contact Support Button */}
          <div className="auth-whatsapp-box">
            <a
              href={`https://wa.me/201207337330?text=${encodeURIComponent('مرحباً خدمة العملاء، أحتاج مساعدة بخصوص تسجيل الدخول في منصة oto students')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp-auth"
              title="تواصل مباشرة عبر واتساب"
            >
              <WhatsAppIcon size={20} />
              <span>تواصل مع الدعم عبر واتساب (01207337330)</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
